import React from 'react';
import { useTranslation } from 'react-i18next';
import { Target, Plus, Trash2 } from 'lucide-react';

import { SelectField, TextField, IconButton, Button } from '@dopomogai/ui';
import type { Goal, KPIObjective } from '@dopomogai/types';

interface KPIObjectiveEditorProps {
  objectives: KPIObjective[];
  availableGoals: Goal[];
  onObjectivesChange: (newObjectives: KPIObjective[]) => void;
  // --- FIX: Add the portalContainer prop to the interface ---
  /**
   * The DOM element to which dropdowns should be portaled.
   * This is passed down from the parent modal to solve z-index issues.
   */
  portalContainer?: HTMLDivElement | null;
}

/**
 * A specialized component for editing the list of KPI objectives for a sprint.
 */
export const KPIObjectiveEditor: React.FC<KPIObjectiveEditorProps> = ({
  objectives,
  availableGoals,
  onObjectivesChange,
  portalContainer, // --- FIX: Destructure the new prop ---
}) => {
  const { t } = useTranslation('admin_sprints');

  const goalOptions = availableGoals.map(g => ({ value: g._id, label: g.name }));

  const handleObjectiveChange = (index: number, field: keyof Omit<KPIObjective, 'id'>, value: string | number) => {
    const newObjectives = [...objectives];
    if (field === 'target_value') {
      newObjectives[index] = { ...newObjectives[index], [field]: Number(value) || 0 };
    } else {
      newObjectives[index] = { ...newObjectives[index], [field]: value };
    }
    onObjectivesChange(newObjectives);
  };

  const addObjective = () => {
    // A new objective might not have a real goal_id yet, so using an empty string is appropriate.
    const newObjective: KPIObjective = { goal_id: '', target_value: 0 };
    onObjectivesChange([...objectives, newObjective]);
  };

  const removeObjective = (index: number) => {
    const newObjectives = objectives.filter((_, i) => i !== index);
    onObjectivesChange(newObjectives);
  };

  return (
    <div>
      <h4 className="text-md font-semibold text-slate-700 mb-2 flex items-center gap-2">
        <Target size={18} />
        {t('editor.kpiObjectivesLabel')}
      </h4>
      <div className="space-y-4 p-4 border-2 border-dashed rounded-xl bg-slate-50">
        {objectives.length === 0 && (
          <p className="text-center text-sm text-slate-500 italic">{t('editor.kpiNoObjectives')}</p>
        )}
        {objectives.map((obj, index) => (
          <div key={index} className="flex items-end gap-3 p-3 bg-white border rounded-lg shadow-sm">
            <div className="flex-grow">
                <SelectField
                    name={`kpi-goal-${index}`}
                    label={t('editor.kpiGoalLabel')}
                    value={obj.goal_id}
                    onChange={(val) => handleObjectiveChange(index, 'goal_id', val as string)}
                    options={goalOptions}
                    placeholder={t('editor.kpiGoalPlaceholder')}
                    // --- FIX: Pass the portalContainer prop down to the SelectField ---
                    // This ensures its dropdown will render inside the modal.
                    portalContainer={portalContainer}
                />
            </div>
            <div className="w-40">
                <TextField
                    name={`kpi-target-${index}`}
                    label={t('editor.kpiTargetLabel')}
                    type="number"
                    value={String(obj.target_value)}
                    onChange={(e) => handleObjectiveChange(index, 'target_value', e.target.value)}
                />
            </div>
            <IconButton
              icon={<Trash2 size={16} />}
              onClick={() => removeObjective(index)}
              variant="danger"
              tooltip={t('editor.kpiRemoveTooltip')}
            />
          </div>
        ))}
        <Button
          textKey="admin_sprints:editor.kpiAddButton"
          variant="secondary"
          icon={<Plus size={16} />}
          onClick={addObjective}
        />
      </div>
    </div>
  );
};